import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { MainLayout } from './layouts/MainLayout';
import { PublicLayout } from './layouts/PublicLayout';
import { Spinner } from './components/common';
import { LegalPage } from './pages/shared/LegalPage';
import { FreePage } from './pages/landing/FreePage';
import { HomePage } from './pages/landing/HomePage';
import { AboutPage } from './pages/landing/AboutPage';
import { FeaturesPage } from './pages/landing/FeaturesPage';
import { ContactPage } from './pages/landing/ContactPage';

// ==================== PAGE IMPORTS ====================
import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  Dashboard,
  UsersPage,
  FlagsPage,
  IntegrityPage,
  GamificationConfigPage,
  AnalyticsPage,
  OrganizationsPage,
  MusclesPage,
  InsightsPage,
  PRProgression,
  GoalsPage,
  TrainerClientsPage,
  ClientProgressReportPage,
  PointsPage,
  BadgesPage,
  TeamsPage,
  NotificationsPage,
  SessionsPage,
  LeaderboardPage,
  RewardsPage,
  ChallengesPage,
} from './pages';

// ==================== FEATURE COMPONENTS ====================
import { ExerciseLibrary } from './components/exercises/ExerciseLibrary';
import { PersonalRecords } from './components/exercises/PersonalRecords';
import { WorkoutTemplates } from './components/workouts/WorkoutTemplates';
import { WorkoutLogging } from './components/workouts/WorkoutLogging';
import { WorkoutScheduler } from './components/workouts/WorkoutScheduler';
import { ProgressTracking } from './components/progress/ProgressTracking';
import { SocialFeed } from './components/social/SocialFeed';
import { DirectMessages } from './components/messages/DirectMessages';

// ==================== WRAPPER COMPONENTS ====================
// Simple wrappers for feature components that need user context
const ExercisesPage = () => <ExerciseLibrary />;
const WorkoutTemplatesPage = () => {
  const { user } = useAuth();
  return <WorkoutTemplates userRole={user?.role} />;
};
const ProgressPage = () => <ProgressTracking />;
const PersonalRecordsPage = () => <PersonalRecords />;
const WorkoutLoggingPage = () => <WorkoutLogging />;
const WorkoutSchedulerPage = () => <WorkoutScheduler />;
const SocialFeedPage = () => {
  const { user } = useAuth();
  return <SocialFeed currentUser={user} />;
};
const DirectMessagesPage = () => {
  const { user } = useAuth();
  return <DirectMessages currentUser={user} />;
};

// ==================== PROTECTED ROUTE ====================
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <MainLayout>{children}</MainLayout>;
};

// ==================== MAIN APP ====================
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Protected routes */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          
          {/* Admin routes */}
          <Route path="/users" element={<ProtectedRoute allowedRoles={['admin', 'master_admin']}><UsersPage /></ProtectedRoute>} />
          <Route path="/gamification" element={<ProtectedRoute allowedRoles={['admin', 'master_admin']}><GamificationConfigPage /></ProtectedRoute>} />
          <Route path="/flags" element={<ProtectedRoute allowedRoles={['admin', 'master_admin']}><FlagsPage /></ProtectedRoute>} />
          <Route path="/integrity" element={<ProtectedRoute allowedRoles={['admin', 'master_admin']}><IntegrityPage /></ProtectedRoute>} />
          <Route path="/organizations" element={<ProtectedRoute allowedRoles={['admin', 'master_admin']}><OrganizationsPage /></ProtectedRoute>} />
           <Route path="/analytics" element={<ProtectedRoute allowedRoles={['admin', 'master_admin']}><AnalyticsPage /></ProtectedRoute>} />
           <Route path="/insights/muscles" element={<ProtectedRoute><MusclesPage /></ProtectedRoute>} />
           <Route path="/insights" element={<ProtectedRoute><InsightsPage /></ProtectedRoute>} />
          <Route path="/prs/progression" element={<ProtectedRoute><PRProgression /></ProtectedRoute>} />
          <Route path="/progress/goals" element={<ProtectedRoute><GoalsPage /></ProtectedRoute>} />
          
          {/* Trainer routes */}
          <Route path="/clients" element={<ProtectedRoute allowedRoles={['trainer']}><TrainerClientsPage /></ProtectedRoute>} />
          <Route path="/clients/:clientId/report" element={<ProtectedRoute allowedRoles={['trainer', 'admin', 'master_admin']}><ClientProgressReportPage /></ProtectedRoute>} />
          
          {/* Client routes */}
          <Route path="/points" element={<ProtectedRoute allowedRoles={['client']}><PointsPage /></ProtectedRoute>} />
          <Route path="/badges" element={<ProtectedRoute allowedRoles={['client']}><BadgesPage /></ProtectedRoute>} />
          <Route path="/team" element={<ProtectedRoute allowedRoles={['client']}><TeamsPage /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          
          {/* Shared routes */}
          <Route path="/sessions" element={<ProtectedRoute><SessionsPage /></ProtectedRoute>} />
          <Route path="/rewards" element={<ProtectedRoute><RewardsPage /></ProtectedRoute>} />
          <Route path="/challenges" element={<ProtectedRoute><ChallengesPage /></ProtectedRoute>} />
          <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
          
          {/* Feature routes - Exercises, Workouts, Progress */}
          <Route path="/exercises" element={<ProtectedRoute><ExercisesPage /></ProtectedRoute>} />
          <Route path="/templates" element={<ProtectedRoute allowedRoles={['admin', 'master_admin', 'trainer', 'client']}><WorkoutTemplatesPage /></ProtectedRoute>} />
          <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
          <Route path="/prs" element={<ProtectedRoute><PersonalRecordsPage /></ProtectedRoute>} />
          <Route path="/workout-log" element={<ProtectedRoute><WorkoutLoggingPage /></ProtectedRoute>} />
          <Route path="/schedule" element={<ProtectedRoute><WorkoutSchedulerPage /></ProtectedRoute>} />
          <Route path="/feed" element={<ProtectedRoute><SocialFeedPage /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><DirectMessagesPage /></ProtectedRoute>} />
          
          {/* Public legal pages (F2) */}
          <Route path="/legal/terms" element={<PublicLayout><LegalPage type="terms" /></PublicLayout>} />
          <Route path="/legal/privacy" element={<PublicLayout><LegalPage type="privacy" /></PublicLayout>} />
          <Route path="/legal/cookies" element={<PublicLayout><LegalPage type="cookies" /></PublicLayout>} />

          {/* B0: Free-forever public page (replaces /pricing) */}
          <Route path="/free" element={<PublicLayout><FreePage /></PublicLayout>} />

          {/* Public marketing pages */}
          <Route path="/features" element={<PublicLayout><FeaturesPage /></PublicLayout>} />
          <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
          <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />

          {/* Marketing landing at the root URL */}
          <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />

          {/* Catch-all: any unknown path under / lands on the marketing
              landing too. Real SPA routes (e.g. /login, /register,
              /dashboard) match above before this fallback runs. */}
          <Route path="*" element={<PublicLayout><HomePage /></PublicLayout>} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
